import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorAlert, LoadingState } from '../../components/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { shootPlanService, extractApiError, extractFieldErrors } from '../../api/services';
import { WIZARD_STEPS } from '../../constants/wizardOptions';
import { statusMeta } from '../../constants/statusMeta';
import { pruneEmpty, formatDate, formatClock } from '../../utils/format';

import StepShootDetails from './StepShootDetails';
import StepReels from './StepReels';
import StepPhotos from './StepPhotos';
import StepCrew from './StepCrew';
import StepBudget from './StepBudget';
import StepReview from './StepReview';
import StepPrintDetails from './StepPrintDetails';
import StepFeedback from './StepFeedback';

import './WizardShell.css';
import './Wizard.css';

const EMPTY_FORM = {
  title: '',
  client_name: '',
  brand: '',
  department: '',
  location: '',
  shoot_date: '',
  call_time: '',
  wrap_time: '',
  status: 'DRAFT',
  completion_percent: 0,
  brief: '',
  client_notified: false,
};

const NULLABLE_TIME_FIELDS = ['call_time', 'wrap_time', 'shoot_date'];

function stepComplete(key, plan) {
  if (!plan) return false;
  switch (key) {
    case 'details':
      return !!(plan.title && plan.shoot_date);
    case 'reels':
      return (plan.reels || []).length > 0;
    case 'photos':
      return (plan.photos || []).length > 0;
    case 'crew':
      return (plan.crew || []).length > 0;
    case 'budget':
      return (plan.budget_items || []).length > 0 || (plan.travel_expenses || []).length > 0;
    case 'review':
      // Matches StepReview.js's own "Step 2: Production Head Approval" done
      // state -- the sidebar checkmark and the Approval Workflow section
      // agree on what "Review & Approval complete" means.
      return ['APPROVED', 'SHOOT_COMPLETED', 'ARCHIVED'].includes(plan.status);
    case 'print':
      return !!plan.print_previewed_at;
    case 'feedback':
      return (plan.feedback || []).length > 0;
    default:
      return false;
  }
}

export default function ShootPlanWizard({ create = false }) {
  const { id } = useParams();
  const { isElevated, selectedDepartment } = useAuth();
  const { showToast, showError } = useToast();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [, setLoading] = useState(!create);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState('details');

  // No visible department picker in Shoot Details (the source design has no
  // department concept at all) -- silently stamp it from context instead:
  // the user's own department, or whichever department an elevated user is
  // currently previewing as. `selectedDepartment` already resolves to the
  // user's own department for non-elevated users.
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    department: selectedDepartment || '',
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const load = useCallback(() => {
    if (create || !id) return;
    setLoading(true);
    shootPlanService
      .get(id)
      .then((data) => {
        setPlan(data);
        setForm({
          title: data.title,
          client_name: data.client_name,
          brand: data.brand || '',
          department: data.department,
          location: data.location,
          shoot_date: data.shoot_date || '',
          call_time: data.call_time || '',
          wrap_time: data.wrap_time || '',
          status: data.status,
          completion_percent: data.completion_percent,
          brief: data.brief,
          client_notified: data.client_notified,
        });
        setError('');
      })
      .catch((err) => setError(extractApiError(err, 'Could not load this shoot plan.')))
      .finally(() => setLoading(false));
  }, [create, id]);

  useEffect(load, [load]);

  // Autosave Step 1's own fields on change, debounced.
  useEffect(() => {
    if (create || !plan) return;
    const timer = setTimeout(() => {
      const payload = pruneEmpty(form, NULLABLE_TIME_FIELDS);
      setSaving(true);
      shootPlanService
        .patch(plan.id, payload)
        .then((updated) => {
          // Merge the saved fields back into `plan` -- not just `form` --
          // so anything deriving from `plan` (e.g. Review & Approval's
          // "Client notified" warning) reflects the save immediately
          // instead of only after a full reload re-fetches the plan.
          setPlan((prev) => (prev ? { ...prev, ...updated } : prev));
          setLastSaved(new Date());
        })
        .catch((err) => {
          const flattened = extractFieldErrors(err);
          setFieldErrors(flattened);
        })
        .finally(() => setSaving(false));
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleCreate = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const payload = pruneEmpty(form, NULLABLE_TIME_FIELDS);
      const created = await shootPlanService.create(payload);
      showToast('Shoot plan created');
      navigate(`/shoot-plans/${created.id}`, { replace: true });
    } catch (err) {
      // Always surface a toast, even when a field error also gets set below --
      // not every field this API can reject (e.g. client_name, which has no
      // input of its own until a Brand is picked) has an inline error slot in
      // the form, so relying on fieldErrors alone can fail completely silently.
      showError(extractApiError(err, 'Could not create this shoot plan.'));
      setFieldErrors(extractFieldErrors(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const payload = pruneEmpty(form, NULLABLE_TIME_FIELDS);
      const updated = await shootPlanService.patch(plan.id, payload);
      setPlan((prev) => (prev ? { ...prev, ...updated } : prev));
      setLastSaved(new Date());
      showToast('Draft saved');
    } catch (err) {
      showError(extractApiError(err, 'Could not save draft.'));
      setFieldErrors(extractFieldErrors(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!window.confirm('Delete this shoot plan and everything in it? This cannot be undone.')) return;
    try {
      await shootPlanService.remove(plan.id);
      showToast('Shoot plan deleted');
      navigate('/shoot-plans', { replace: true });
    } catch (err) {
      showError(extractApiError(err, 'Could not delete this shoot plan.'));
    }
  };

  const trackedSteps = useMemo(
    () => WIZARD_STEPS.filter((s) => s.key !== 'review' && s.key !== 'print' && s.key !== 'feedback'),
    []
  );

  // Single source of truth: the same field-level ratio the backend computes
  // for the Shoot Plans list cards (real required-fields-completed / total,
  // across every Reel/Shot -- not "does at least one reel exist"). Reusing
  // it here instead of a separate frontend calculation is what keeps the
  // sidebar ring, the "at a glance" panel, and the dashboard cards from
  // ever disagreeing with each other.
  const progressPercent = plan?.completion_percent ?? 0;

  const attentionCount = useMemo(() => {
    if (!plan) return 0;
    return trackedSteps.filter((s) => !stepComplete(s.key, plan)).length;
  }, [plan, trackedSteps]);

  // Whichever step comes after the one currently open, in the fixed 1-8
  // order -- undefined on the last step (Feedback), which is exactly when
  // no "Next" button should render.
  const nextStep = WIZARD_STEPS[WIZARD_STEPS.findIndex((s) => s.key === activeStep) + 1];

  if (create) {
    return (
      <div className="rr-wiz-page">
        <div className="rr-wiz">
          <div className="rr-wiz__topbar">
            <button className="rr-wiz__back" onClick={() => navigate('/shoot-plans')}>
              ← Shoot Plans
            </button>
            <div className="rr-wiz__title">New Shoot Plan</div>
          </div>
          <div style={{ padding: 28, maxWidth: 720 }}>
            <StepShootDetails plan={null} form={form} setForm={setForm} fieldErrors={fieldErrors} onCrewChanged={() => {}} />
            <button type="button" className="rr-toggle-btn rr-toggle-btn--active" disabled={saving} onClick={handleCreate}>
              {saving ? 'Creating…' : 'Create shoot plan'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Guard on `plan` itself, not the `loading` flag -- React Router can keep
  // this same component instance mounted across the create -> detail
  // navigate() (same component type, different route), so `loading`'s
  // stale value from the create-mode render isn't trustworthy here.
  if (!plan) {
    return (
      <div className="rr-wiz-page">
        {error ? <ErrorAlert message={error} /> : <LoadingState label="Loading shoot plan" />}
      </div>
    );
  }

  const meta = statusMeta(plan.status);

  const stepProps = { plan, onChanged: load };

  return (
    <div className="rr-wiz-page">
      <div className="rr-wiz">
        <div className="rr-wiz__topbar">
          <button className="rr-wiz__back" onClick={() => navigate('/shoot-plans')}>
            ← Shoot Plans
          </button>
          <div className="rr-wiz__title">{plan.title}</div>
          <span className="rr-pill" style={{ background: meta.bg, color: meta.fg }}>
            {meta.icon} {meta.label}
          </span>
          <div className="rr-wiz__spacer" />
        </div>

        <div className="rr-wiz__pills">
          {WIZARD_STEPS.map((s) => (
            <button
              key={s.key}
              className={`rr-wiz__pill${activeStep === s.key ? ' rr-wiz__pill--active' : ''}`}
              onClick={() => setActiveStep(s.key)}
            >
              {s.num} · {s.label}
            </button>
          ))}
        </div>

        <div className="rr-wiz__body">
          <div className="rr-wiz__nav">
            <div className="rr-wiz__ring-wrap">
              <div className="rr-wiz__ring" style={{ background: `conic-gradient(#0e0e0e ${progressPercent}%, #e4e2dd 0)` }}>
                <div className="rr-wiz__ring-inner">{progressPercent}%</div>
              </div>
              <span className="rr-wiz__ring-label">complete</span>
            </div>
            <div className="rr-wiz__steps">
              {WIZARD_STEPS.map((s) => (
                <button
                  key={s.key}
                  className={`rr-wiz__step${activeStep === s.key ? ' rr-wiz__step--active' : ''}`}
                  onClick={() => setActiveStep(s.key)}
                >
                  <span>
                    {s.num} · {s.label}
                  </span>
                  {stepComplete(s.key, plan) && <span className="rr-wiz__step-check">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="rr-wiz__content">
            {activeStep === 'details' && (
              <StepShootDetails
                plan={plan}
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                onCrewChanged={load}
              />
            )}
            {activeStep === 'reels' && <StepReels {...stepProps} isElevated={isElevated} />}
            {activeStep === 'photos' && <StepPhotos {...stepProps} isElevated={isElevated} />}
            {activeStep === 'crew' && <StepCrew {...stepProps} />}
            {activeStep === 'budget' && <StepBudget {...stepProps} />}
            {activeStep === 'review' && (
              <StepReview {...stepProps} goToStep={setActiveStep} isElevated={isElevated} onDeletePlan={handleDeletePlan} />
            )}
            {activeStep === 'print' && <StepPrintDetails plan={plan} goToStep={setActiveStep} onChanged={load} />}
            {activeStep === 'feedback' && <StepFeedback {...stepProps} isElevated={isElevated} />}

            <div className="rr-wiz__next rr-print-hide">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="rr-wiz__savetext">{saving ? 'Saving…' : lastSaved ? `Saved · ${formatClock(lastSaved)}` : ''}</span>
                <button type="button" className="rr-wiz__btn" disabled={saving} onClick={handleSaveDraft}>
                  Save Draft
                </button>
              </div>
              {nextStep && (
                <button
                  type="button"
                  className="rr-toggle-btn rr-toggle-btn--active"
                  onClick={() => {
                    // The wizard swaps step content in place (no real page
                    // navigation), so without this the browser keeps
                    // whatever scroll offset it was at -- landing mid-way or
                    // at the bottom of the next section instead of its top.
                    setActiveStep(nextStep.key);
                    window.scrollTo({ top: 0 });
                  }}
                >
                  Next → {nextStep.label}
                </button>
              )}
            </div>
          </div>

          {activeStep === 'details' && (
            <div className="rr-wiz__glance">
              <div className="rr-wiz__glance-title">At a glance</div>
              <div className="rr-wiz__glance-row">
                <span>Brand</span>
                <b>{plan.brand_name || plan.client_name || '—'}</b>
              </div>
              <div className="rr-wiz__glance-row">
                <span>Date</span>
                <b>{plan.shoot_date ? formatDate(plan.shoot_date) : '—'}</b>
              </div>
              <div className="rr-wiz__glance-row">
                <span>Models</span>
                <b>{(plan.plan_models || []).length} added</b>
              </div>
              <div className="rr-wiz__glance-row">
                <span>Completion</span>
                <b>{progressPercent}%</b>
              </div>
              {attentionCount > 0 && (
                <div className="rr-wiz__glance-warning">
                  ⚠ {attentionCount} item{attentionCount === 1 ? '' : 's'} need{attentionCount === 1 ? 's' : ''} attention
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
